import { create } from 'zustand';
import axiosInstance from '../api/axios';

// LocalStorage-Key für Gäste-Wishlist (persistiert per Cookie-ähnlicher Weise
// im Browser, kein Login nötig). Bei Anmeldung wird sie in den Account gemerged.
const LS_KEY = 'hys_wishlist_v1';

const readLocal = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
};

const writeLocal = (ids) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(Array.from(new Set(ids))));
  } catch {
    /* ignore quota / private-mode errors */
  }
};

const isAuthed = () => {
  try {
    return !!localStorage.getItem('token');
  } catch {
    return false;
  }
};

const useWishlistStore = create((set, get) => ({
  // Beim Laden direkt aus LocalStorage hydrieren, damit der Herz-Zähler
  // im Header sofort korrekt ist (auch für Gäste).
  wishlistIds: readLocal(),
  loading: false,

  // Wishlist vom Backend holen (nur wenn eingeloggt). Für Gäste = LocalStorage.
  // Beim ersten Login wird die Gäste-Wishlist in den Account gemerged.
  fetchWishlist: async () => {
    if (!isAuthed()) {
      set({ wishlistIds: readLocal(), loading: false });
      return;
    }
    try {
      set({ loading: true });
      const localIds = readLocal();
      // Merge: lokale IDs erst POST'en, dann Backend-State übernehmen
      if (localIds.length > 0) {
        await Promise.all(
          localIds.map((id) =>
            axiosInstance.post(`/wishlist/${id}`).catch(() => null),
          ),
        );
      }
      const response = await axiosInstance.get('/wishlist');
      const merged = response.data.product_ids || [];
      set({ wishlistIds: merged, loading: false });
      writeLocal(merged); // LocalStorage als Cache spiegeln
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      // Fallback auf lokale Werte, damit UI nicht leer wird
      set({ wishlistIds: readLocal(), loading: false });
    }
  },

  addToWishlist: async (productId) => {
    // Optimistisches Update — funktioniert für Gäste UND Eingeloggte
    set((state) => {
      if (state.wishlistIds.includes(productId)) return state;
      const next = [...state.wishlistIds, productId];
      writeLocal(next);
      return { wishlistIds: next };
    });
    if (isAuthed()) {
      try {
        await axiosInstance.post(`/wishlist/${productId}`);
      } catch (error) {
        console.error('Error adding to wishlist:', error);
      }
    }
    return true;
  },

  removeFromWishlist: async (productId) => {
    set((state) => {
      const next = state.wishlistIds.filter((id) => id !== productId);
      writeLocal(next);
      return { wishlistIds: next };
    });
    if (isAuthed()) {
      try {
        await axiosInstance.delete(`/wishlist/${productId}`);
      } catch (error) {
        console.error('Error removing from wishlist:', error);
      }
    }
    return true;
  },

  toggleWishlist: async (productId) => {
    const { wishlistIds, addToWishlist, removeFromWishlist } = get();
    if (wishlistIds.includes(productId)) {
      return await removeFromWishlist(productId);
    }
    return await addToWishlist(productId);
  },

  isInWishlist: (productId) => get().wishlistIds.includes(productId),

  getWishlistCount: () => get().wishlistIds.length,

  // Bei Logout: Backend-Session-State ist weg, aber die LocalStorage-Wishlist
  // bleibt bewusst erhalten (Gast-Erfahrung: „meine gemerkten Sachen sind
  // beim nächsten Besuch noch da").
  clearWishlist: () => {
    set({ wishlistIds: readLocal(), loading: false });
  },
}));

export default useWishlistStore;
